import React, { useEffect, useState } from 'react';
import './dashboard.css';
import { MdAccessTimeFilled } from 'react-icons/md';
import { IoMdTime } from 'react-icons/io';
import { LuLayoutDashboard } from "react-icons/lu";
import axios from 'axios';
 
const Dashboard = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [error, setError] = useState('');
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkInStatus, setCheckInStatus] = useState('');
  const [checkInError, setCheckInError] = useState('');
  const [approvedLeaveCount, setApprovedLeaveCount] = useState(0);
  const [attendancePercentage, setAttendancePercentage] = useState(0);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [attendanceError, setAttendanceError] = useState('');
 
  // Function to format time
  const formatTime = (datetime) => {
    if (!datetime || datetime === 'No Check-in Yet') return '-';
    const date = new Date(datetime);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };
 
  useEffect(() => {
    // Fetch the role from local storage
    const userRole = localStorage.getItem('role');
    const userId = localStorage.getItem('userId');
   
    if (!userRole) {
      setError('Role is not found in local storage');
      return;
    }
 
    if (!userId) {
      setError('User ID not found in local storage');
      return;
    }
 
    // Fetch announcements
    const fetchAnnouncements = async () => {
      try {
        const response = await axios.get('https://backend.ems.hiradentallab.com/api/announcements/role/announcement', {
          params: { role: userRole }
        });
        setAnnouncements(response.data);
      } catch (error) {
        setError('Error fetching announcements');
        console.error(error);
      }
    };
 
    // Fetch today's check-in details
    const fetchCheckInTime = async () => {
      try {
        const response = await axios.get('https://backend.ems.hiradentallab.com/api/attendance/today', {
          params: { userId },
        });
        const checkIn = response.data.data.checkIn || 'No Check-in Yet';
        const shiftStart = response.data.data.shiftStart;
 
        if (checkIn === 'No Check-in Yet') {
          setCheckInTime(checkIn);
          setCheckInStatus('no-check-in');
        } else {
          setCheckInTime(checkIn);
          // Check if the user is early, on-time, or late
          const checkInTimeObj = new Date(`1970-01-01T${checkIn}`);
          const shiftStartTimeObj = new Date(`1970-01-01T${shiftStart}`);
          if (checkInTimeObj > shiftStartTimeObj) {
            setCheckInStatus('late');
          } else if (checkInTimeObj < shiftStartTimeObj) {
            setCheckInStatus('early');
          } else {
            setCheckInStatus('on-time');
          }
        }
      } catch (error) {
        setCheckInError('Error fetching check-in time');
        console.error(error);
      }
    };
 
    // Fetch the approved leaves count for the user
    const fetchApprovedLeaveCount = async () => {
      try {
        const response = await axios.post('https://backend.ems.hiradentallab.com/api/leaves/count-approved-leaves', {
          userId,
        });
        setApprovedLeaveCount(response.data.count);
      } catch (error) {
        setError('Error fetching approved leaves count');
        console.error(error);
      }
    };
 
    const fetchMonthlyAttendance = async () => {
      setAttendanceLoading(true);
      try {
        const response = await axios.get('https://backend.ems.hiradentallab.com/api/attendance/monthly-attendance-percentage', {
          params: { userId }
        });
        setAttendancePercentage(response.data.data.attendancePercentage);
        setAttendanceError('');
      } catch (error) {
        setAttendanceError('Error fetching attendance percentage');
        console.error('Attendance percentage error:', error);
      } finally {
        setAttendanceLoading(false);
      }
    };
 
    // ... (other fetch functions remain the same)
    fetchAnnouncements();
    fetchCheckInTime();
    fetchApprovedLeaveCount();
    fetchMonthlyAttendance();
  }, []);
 
  // Determine the color based on check-in status
  const getCheckInCardClass = () => {
    if (checkInStatus === 'late') {
      return 'checkin-card-late';
    } else if (checkInStatus === 'early' || checkInStatus === 'on-time') {
      return 'checkin-card-early';
    } else {
      return '';
    }
  };
 
  return (
    <div className='container-fluid'>
      <div style={{ padding: '10px' }}>
        <div className="header">
          <h1 className='dashboard-title'> <LuLayoutDashboard /> Dashboard</h1>
          <div className="header-links">
            <a href='/' className="header-link">Not Checked-In <MdAccessTimeFilled /></a>
            <button className="header-button">10:00 pm <IoMdTime /></button>
            <button className="header-button">00:00 <IoMdTime /></button>
          </div><hr />
        </div>
        <div className="container-fluid">
          <section>
            <div className="row">
              {/* Dashboard Cards */}
              <div className="col-lg-4 p-1">
                <div className="card" id="dashboard-card">
                  <div className="card-header" id="dashboard-card-header">
                    This Month's Attendance <i className="fa fa-percent"></i>
                  </div>
                  <div className="card-body text-center d-flex justify-content-center align-items-center" id="dashboard-card-body">
                    <div id="announcements">
                      {attendanceLoading ? (
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : attendanceError ? (
                        <h5 className="card-title">{attendanceError}</h5>
                      ) : (
                        <>
                          <h5 className="card-title">{attendancePercentage}%</h5>
                          <p className="card-text">Attendance Of Month</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="card-footer">
                    <a href="/current-atd" className="btn btn-primary">See Attendance</a>
                  </div>
                </div>
              </div>
             
              <div className="col-lg-4 p-1">
                <div className="card" id="dashboard-card">
                  <div className="card-header" id="dashboard-card-header">
                    Announcements <i className="fa fa-volume-up"></i>
                  </div>
                  <div className="card-body" id="dashboard-card-body">
                    <div id="announcements">
                      {error ? (
                        <h5 className="card-title">{error}</h5>
                      ) : announcements.length > 0 ? (
                        announcements.map((announcement, index) => (
                          <div key={index}>
                            <h5 className="card-title">{announcement.title}</h5>
                            <p className="card-text">{announcement.content}</p>
                          </div>
                        ))
                      ) : (
                        <h5 className="card-title">No Announcements For Today</h5>
                      )}
                    </div>
                  </div>
                  <div className="card-footer">
                    <a href="/announcement" className="btn btn-primary">See Announcements</a>
                  </div>
                </div>
              </div>
             
              <div className="col-lg-4 p-1">
                <div className="card" id="dashboard-card">
                  <div className="card-header" id="dashboard-card-header">
                    My Provident Fund <i className="fa fa-newspaper-o"></i>
                  </div>
                  <div className="card-body text-center d-flex justify-content-center align-items-center" id="dashboard-card-body">
                    <div id="announcements">
                      <h5 className="card-title">26000</h5>
                      <p className="card-text">Provident Fund</p>
                    </div>
                  </div>
                  <div className="card-footer">
                    <a href="/provident-fund" className="btn btn-primary">See Provident Fund</a>
                  </div>
                </div>
              </div>
             
              <div className="col-lg-4 p-1">
                <div className={`card ${getCheckInCardClass()}`} id="dashboard-card">
                  <div className="card-header" id="dashboard-card-header">
                    Check-in-Time <i className="fa fa-newspaper-o"></i>
                  </div>
                  <div className="card-body text-center d-flex justify-content-center align-items-center" id="dashboard-card-body">
                    <div id="announcements">
                      {checkInError ? (
                        <h5 className="card-title">{checkInError}</h5>
                      ) : (
                        <h5 className="card-title">{formatTime(checkInTime)}</h5>
                      )}
                      <p className="card-text">My Check-in Time</p>
                    </div>
                  </div>
                  <div className="card-footer">
                    <a href="/time-history" className="btn btn-primary">See Checking Time Today</a>
                  </div>
                </div>
              </div>
             
              <div className="col-lg-4 p-1">
                <div className="card" id="dashboard-card">
                  <div className="card-header" id="dashboard-card-header">
                    Approved Leaves <i className="fa fa-newspaper-o"></i>
                  </div>
                  <div className="card-body text-center d-flex justify-content-center align-items-center" id="dashboard-card-body">
                    <div id="announcements">
                      <h5 className="card-title">{approvedLeaveCount}</h5>
                      <p className="card-text">Leave Approved</p>
                    </div>
                  </div>
                  <div className="card-footer">
                    <a href="/approved-leave" className="btn btn-primary">See Approved Leaves</a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
 
export default Dashboard; 